import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const InventoryAlerts = () => {
  const alerts = [
    {
      id: 1,
      type: 'critical',
      product: 'AirPods Pro 2nd Gen',
      sku: 'APP2-USB-C',
      currentStock: 0,
      reorderLevel: 10,
      message: 'Product is out of stock',
      timestamp: '2024-09-23T14:30:00Z',
      category: 'AirPods'
    },
    {
      id: 2,
      type: 'warning',
      product: 'Wireless Mouse Pro',
      sku: 'MSE-WL-PRO',
      currentStock: 3,
      reorderLevel: 10,
      message: 'Stock level below reorder point',
      timestamp: '2024-09-23T13:15:00Z',
      category: 'Accessories'
    },
    {
      id: 3,
      type: 'warning',
      product: 'MacBook Pro 16"',
      sku: 'MBP16-M3-1TB',
      currentStock: 12,
      reorderLevel: 15,
      message: 'Stock level approaching minimum',
      timestamp: '2024-09-23T12:00:00Z',
      category: 'Laptops'
    },
    {
      id: 4,
      type: 'info',
      product: 'iPhone 15 Pro Max',
      sku: 'IPH15PM-256-TB',
      currentStock: 45,
      reorderLevel: 20,
      message: 'Restock recommended for high-demand item',
      timestamp: '2024-09-23T10:45:00Z',
      category: 'Phones'
    },
    {
      id: 5,
      type: 'critical',
      product: 'Gaming Headset RGB',
      sku: 'HDS-RGB-GM',
      currentStock: 1,
      reorderLevel: 15,
      message: 'Critical stock level - immediate reorder required',
      timestamp: '2024-09-23T09:30:00Z',
      category: 'Accessories'
    }
  ];

  const getAlertIcon = (type) => {
    const icons = {
      critical: 'AlertTriangle',
      warning: 'AlertCircle',
      info: 'Info'
    };
    return icons?.[type] || 'Info';
  };

  const getAlertColor = (type) => {
    const colors = {
      critical: 'border-l-error bg-error/5 text-error',
      warning: 'border-l-warning bg-warning/5 text-warning',
      info: 'border-l-primary bg-primary/5 text-primary'
    };
    return colors?.[type] || colors?.info;
  };

  const getStockIndicator = (currentStock, reorderLevel) => {
    if (currentStock === 0) return { color: 'error', label: 'Out of Stock' };
    if (currentStock < reorderLevel) return { color: 'warning', label: 'Low Stock' };
    return { color: 'success', label: 'In Stock' };
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="holographic-card rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-xl font-bold text-foreground">Inventory Alerts</h3>
            <div className="flex items-center space-x-2">
              <div className="inventory-pulse high"></div>
              <span className="text-sm text-muted-foreground">Live Monitoring</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="Settings">
              Configure
            </Button>
            <Button variant="outline" size="sm" iconName="RefreshCw">
              Refresh
            </Button>
          </div>
        </div>
      </div>
      {/* Alert Summary */}
      <div className="p-6 border-b border-border bg-muted/10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-error/20 border border-error/30 rounded-lg">
              <Icon name="AlertTriangle" size={20} className="text-error" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {alerts?.filter(alert => alert?.type === 'critical')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Critical Alerts</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-warning/20 border border-warning/30 rounded-lg">
              <Icon name="AlertCircle" size={20} className="text-warning" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {alerts?.filter(alert => alert?.type === 'warning')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Warning Alerts</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/20 border border-primary/30 rounded-lg">
              <Icon name="Info" size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">
                {alerts?.filter(alert => alert?.type === 'info')?.length}
              </p>
              <p className="text-sm text-muted-foreground">Info Alerts</p>
            </div>
          </div>
        </div>
      </div>
      {/* Alerts List */}
      <div className="max-h-96 overflow-y-auto">
        {alerts?.map((alert) => {
          const stockIndicator = getStockIndicator(alert?.currentStock, alert?.reorderLevel);
          
          return (
            <div
              key={alert?.id}
              className={`p-4 border-l-4 border-b border-border last:border-b-0 hover:bg-muted/20 transition-colors ${getAlertColor(alert?.type)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <Icon name={getAlertIcon(alert?.type)} size={20} />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-semibold text-foreground text-sm">{alert?.product}</h4>
                      <span className="text-xs text-muted-foreground">({alert?.sku})</span>
                      <span className="text-xs px-2 py-1 bg-muted/30 rounded-full text-muted-foreground">
                        {alert?.category}
                      </span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">{alert?.message}</p>
                    
                    <div className="flex items-center space-x-4 text-xs">
                      <div className="flex items-center space-x-1">
                        <span className="text-muted-foreground">Current Stock:</span>
                        <span className={`font-medium text-${stockIndicator?.color}`}>
                          {alert?.currentStock}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-muted-foreground">Reorder Level:</span>
                        <span className="font-medium text-foreground">{alert?.reorderLevel}</span>
                      </div>
                      <span className="text-muted-foreground">{formatTimestamp(alert?.timestamp)}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 ml-4">
                  <Button variant="ghost" size="sm" iconName="Package">
                    Reorder
                  </Button>
                  <Button variant="ghost" size="sm" iconName="Edit">
                    Edit
                  </Button>
                  <Button variant="ghost" size="sm" iconName="X">
                    Dismiss
                  </Button>
                </div>
              </div>
              {/* Stock Level Visualization */}
              <div className="mt-3 ml-8">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                  <span>Stock Level</span>
                  <span>{alert?.currentStock} / {alert?.reorderLevel * 2} units</span>
                </div>
                <div className="w-full bg-muted/30 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      alert?.currentStock === 0 ? 'bg-error' :
                      alert?.currentStock < alert?.reorderLevel ? 'bg-warning' : 'bg-success'
                    }`}
                    style={{ 
                      width: `${Math.min((alert?.currentStock / (alert?.reorderLevel * 2)) * 100, 100)}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Footer Actions */}
      <div className="p-4 border-t border-border bg-muted/10">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            {alerts?.length} active alerts
          </span>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" iconName="CheckCircle">
              Mark All Read
            </Button>
            <Button variant="outline" size="sm" iconName="Archive">
              Archive Resolved
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryAlerts;